import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Page from "../../common/layout/Page";
import Control from "../../common/Control";
import Loader from "../../common/Loader";
import Pagination from "../../common/Pagination";
import SaveBtn from "../../common/SaveBtn";
import Table from "../../common/Table";
import tools from "../../../helpers/tools";
import dataService from "../../../helpers/dataService";
import CompteursImport from "./components/CompteursImport";
import StatutImportBadge from "./components/StatutImportBadge";
import { formatDuree } from "./components/importFormatters";

// L'API n'expose pas de route de constantes pour ces deux filtres : les
// libellés reprennent mot pour mot ceux du Domain (TypeImport / StatutImport)
// puisqu'aucune donnée d'import ne circule ici (ce ne sont que des options de
// filtre, pas des données affichées venant de l'API).
const TYPES_IMPORT = [
  { id: "ALLOCATAIRES_SID", name: "Allocataires (flux SID)" },
  { id: "ALLOCATAIRES_STOCK_SID", name: "Allocataires (stock SID)" },
  { id: "ANONYMISATION_CAF", name: "Anonymisation (fichier CAF)" },
];

const STATUTS_IMPORT = [
  { id: "EN_ATTENTE", name: "En attente" },
  { id: "EN_COURS", name: "En cours" },
  { id: "TERMINE", name: "Terminé" },
  { id: "TERMINE_AVEC_ERREURS", name: "Terminé avec des erreurs" },
  { id: "ECHEC", name: "Échec" },
];

const FILTRES_VIDES = {
  type: "",
  statut: "",
  nomFichier: "",
  dateDebut: "",
  dateFin: "",
};

const PAGINATION_INITIALE = { page: 1, nbItemsByPage: 25 };

/**
 * La forme de l'état mémorisé a changé en même temps que les filtres. Un agent qui
 * avait déjà ouvert l'écran a l'ancienne forme à plat en session : on la relit sans
 * la faire planter, en ne gardant que les clés qu'on connaît.
 */
function lireEtatMemorise() {
  const vide = { filtres: FILTRES_VIDES, pagination: PAGINATION_INITIALE };
  const etat = tools.getState("ImportList", null);
  if (!etat) return vide;

  let memorise;
  try {
    memorise = JSON.parse(etat.search);
  } catch (e) {
    return vide;
  }

  const filtresMemorises = memorise.filtres ?? memorise;
  const paginationMemorisee = memorise.pagination ?? memorise;
  const garder = (source, modele) =>
    Object.fromEntries(
      Object.keys(modele).map((cle) => [cle, source?.[cle] ?? modele[cle]])
    );

  return {
    filtres: garder(filtresMemorises, FILTRES_VIDES),
    pagination: garder(paginationMemorisee, PAGINATION_INITIALE),
  };
}

const ImportList = () => {
  const etatInitial = lireEtatMemorise();

  // `filtres` est la saisie en cours, `filtresAppliques` ce qui a réellement été
  // envoyé à l'API. Seul le second déclenche une requête : sans cette séparation,
  // chaque frappe dans le champ fichier relancerait la recherche.
  const [filtres, setFiltres] = useState(etatInitial.filtres);
  const [filtresAppliques, setFiltresAppliques] = useState(etatInitial.filtres);
  const [pagination, setPagination] = useState(etatInitial.pagination);

  const [imports, setImports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState(false);
  const [nbPages, setNbPages] = useState(1);
  const [nbResults, setNbResults] = useState(0);

  useEffect(() => {
    tools.saveState(
      "ImportList",
      "search",
      JSON.stringify({ filtres: filtresAppliques, pagination })
    );
    searchImports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtresAppliques, pagination]);

  function searchImports() {
    setIsLoading(true);
    setErrors(false);
    const parPage = parseInt(pagination.nbItemsByPage);
    dataService.post(
      "imports/search",
      {
        type: filtresAppliques.type || null,
        statut: filtresAppliques.statut || null,
        nomFichier: filtresAppliques.nomFichier || null,
        dateDebut: filtresAppliques.dateDebut || null,
        dateFin: filtresAppliques.dateFin || null,
        page: pagination.page,
        parPage,
      },
      (data) => {
        setImports(data.imports);
        setNbResults(data.total);
        setNbPages(Math.max(1, Math.ceil(data.total / parPage)));
      },
      setErrors,
      () => setIsLoading(false)
    );
  }

  function updateFilter(e) {
    const { name, value } = e.target;
    setFiltres((f) => ({ ...f, [name]: value }));
  }

  function appliquerFiltres(e) {
    if (e) e.preventDefault();
    setFiltresAppliques(filtres);
    setPagination((p) => ({ ...p, page: 1 }));
  }

  function viderFiltres() {
    setFiltres(FILTRES_VIDES);
    setFiltresAppliques(FILTRES_VIDES);
    setPagination((p) => ({ ...p, page: 1 }));
  }

  function changePage(i) {
    setPagination((p) => ({ ...p, page: i }));
  }

  function changeNbItemsByPage(i) {
    setPagination({ page: 1, nbItemsByPage: i });
  }

  return (
    <>
      <Page container="container-fluid px-5" title="Suivi des imports">
        <form className="row mb-4" onSubmit={appliquerFiltres}>
          <div className="col-12 col-md-3">
            <Control
              type="select"
              label="Type"
              name="type"
              value={filtres.type}
              change={updateFilter}
              datas={TYPES_IMPORT}
              selectFirstLabel="Tous"
            />
          </div>
          <div className="col-12 col-md-3">
            <Control
              type="select"
              label="Statut"
              name="statut"
              value={filtres.statut}
              change={updateFilter}
              datas={STATUTS_IMPORT}
              selectFirstLabel="Tous"
            />
          </div>
          <div className="col-12 col-md-3">
            <Control
              type="text"
              label="Fichier"
              name="nomFichier"
              value={filtres.nomFichier}
              change={updateFilter}
              placeholder="Nom ou fragment"
            />
          </div>
          <div className="col-12 col-md-3 align-items-center justify-content-end d-flex">
            {nbResults} import{nbResults > 1 ? "s" : ""} trouvé
            {nbResults > 1 ? "s" : ""}
          </div>

          <div className="col-12 col-md-3">
            <Control
              type="date"
              label="Importé du"
              name="dateDebut"
              value={filtres.dateDebut}
              change={updateFilter}
            />
          </div>
          <div className="col-12 col-md-3">
            <Control
              type="date"
              label="Au"
              name="dateFin"
              value={filtres.dateFin}
              change={updateFilter}
            />
          </div>
          <div className="col-12 col-md-6 align-items-end justify-content-end d-flex">
            <button
              type="button"
              className="btn btn-default mr-2 mb-3"
              onClick={viderFiltres}
            >
              Vider les filtres
            </button>
            <SaveBtn
              save={appliquerFiltres}
              text="Rechercher"
              type="btn-primary"
              typeBtn="submit"
              margin="mb-3"
              isSaving={isLoading}
            />
          </div>
        </form>
      </Page>
      <div className="col-12 p-4">
        {errors && errors.other ? (
          <div className="text-danger m-auto text-center">{errors.other}</div>
        ) : isLoading ? (
          <Loader />
        ) : (
          <Table
            bgTransparent
            datas={imports}
            fields={[
              {
                name: "Démarré le",
                path: "demarreLe",
                render: (el) =>
                  el ? tools.formatDate(el, "dd/MM/yyyy HH:mm:ss") : "-",
              },
              { name: "Type", path: "typeLabel" },
              {
                name: "Statut",
                path: "*",
                render: (el) => (
                  <StatutImportBadge
                    statut={el.statut}
                    statutLabel={el.statutLabel}
                  />
                ),
              },
              {
                name: "Fichier",
                path: "nomFichier",
                render: (el) => el || "-",
              },
              {
                name: "Compteurs",
                path: "*",
                render: (el) => (
                  <CompteursImport
                    compteurs={el.compteurs}
                    type={el.type}
                    variant="compact"
                  />
                ),
              },
              {
                name: "Durée",
                path: "dureeMs",
                render: (el) => formatDuree(el),
              },
              {
                name: "Détail",
                path: "id",
                render: (elem) => (
                  <Link
                    className="btn btn-primary btn-sm align-items-center justify-content-center d-flex"
                    to={"/imports/" + elem}
                  >
                    <i className="fa fa-search"></i>
                  </Link>
                ),
              },
            ]}
          />
        )}
        <Pagination
          nbPages={nbPages}
          page={pagination.page}
          changePage={changePage}
          nbItemsByPage={pagination.nbItemsByPage}
          changeNbItemsByPage={changeNbItemsByPage}
        />
      </div>
    </>
  );
};

export default ImportList;
